import {
  UserProfile,
  UserWorkoutSession,
} from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import React, { FunctionComponent, useEffect } from 'react';
import { translate } from 'react-i18next';
import {
  Platform, ScrollView, StyleSheet, View,
} from 'react-native';
import { connect } from 'react-redux';
import headerImage from '../../assets/images/workout-booking-summary.png';
import {
  plannerDayPressedAction, plannerEntryPressedAction,
} from '../../Bookings';
import { BookOrJoinWorkoutButton } from '../../Bookings/components/BookOrJoinWorkoutButton';
import { IAppStore } from '../../HomeWorkouts';
import { WorkoutSessionService } from '../../services';
import Theme from '../../theme';
import { TrackingImageTile } from '../../Tracking/components/TrackingImageTile';
import {
  AppStateFocusedEvents,
} from '../../ui';
import { isWeb } from '../../utils';
import { defaultScale, scaleImageAspectRatio } from '../../utils/scaling';
import { plannerOpened } from '../plannerActions';
import { getWorkoutSectionTiles, WorkoutSectionTile } from '../plannerSelectors';
import { PlannerDay } from './PlannerDay';

interface IProps {
  user: {
    userProfile: UserProfile,
    workoutSessionService: WorkoutSessionService,
  };
  navigation: any;
  t: any;
}

interface IReduxStateProps {
  days: moment.Moment[];
  workoutSessions: any[];
  plannerEntries: WorkoutSectionTile[];
}

interface IDispatchProps {
  plannerFocused(): void;
  plannerDayPressed(day: moment.Moment): void;
  plannerEntryPressed(booking: UserWorkoutSession): void;
}

const PlannerComponent: FunctionComponent<IProps & IDispatchProps & IReduxStateProps> = (
  {
    days,
    user,
    plannerEntries,
    t,
    ...props
  },
) => {
  useEffect(() => {
    props.plannerFocused();
  }, []);

  const openBookingWizard = (dayToBook: moment.Moment, booking: UserWorkoutSession) => {
    if (booking) {
      return props.plannerEntryPressed(booking);
    }
    return props.plannerDayPressed(dayToBook);
  };

  const plannerDidFocus = () => {
    props.plannerFocused();
  };

  return (
    <View style={styles.background}>
      <AppStateFocusedEvents
        onScreenFocus={plannerDidFocus}
        navigation={props.navigation}
      />
      <ScrollView contentContainerStyle={{ paddingBottom: defaultScale(65) }}>
        <TrackingImageTile
          imageSource={headerImage}
          containerStyle={styles.plannerHeaderImageContainer}
          contentContainerStyle={{ paddingBottom: 0 }}
          title={t('plannerHeaderText')}
        />
        {
          days.map((day, index) => (
            <PlannerDay
              entries={plannerEntries}
              day={day}
              onPress={(booking) => openBookingWizard(day, booking)}
              key={index}
              t={t}
              userProfile={user.userProfile}
            />
          ))
        }
      </ScrollView>
      <View>
        <BookOrJoinWorkoutButton
          workoutSessions={props.workoutSessions}
          userProfile={user.userProfile}
          workoutSessionService={user.workoutSessionService}
          t={t}
          navigation={props.navigation}
          analyticsEventName='planner_workout_schedule_request'
          buttonContainer={styles.startWorkoutButtonContainer}
          buttonStyles={styles.startWorkoutButtonStyles}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  background: {
    flex: 1,
    backgroundColor: Theme.WHITE,
  },
  day: {
    height: defaultScale(100),
    marginTop: defaultScale(15),
    overflow: 'hidden',
  },
  startWorkoutButtonContainer: {
    paddingHorizontal: defaultScale(12),
    width: '100%',
    position: 'absolute',
    bottom: defaultScale(9),
  },
  startWorkoutButtonStyles: {
    shadowColor: Platform.OS === 'android' ? Theme.BLACK : 'rgba(0, 0, 0, 0.37)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 10,
    borderRadius: defaultScale(3),
    elevation: Platform.OS === 'android' ? 20 : 0,
    ...isWeb() ? { boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.37)' } : {},
  },
  plannerHeaderImageContainer: {
    width: '100%',
    aspectRatio: scaleImageAspectRatio(1.8),
  },
});

const mapStateToProps = (state: IAppStore, ownProps: IProps): IReduxStateProps => {
  const { userProfile } = ownProps.user;
  return {
    days: state.planner.days,
    workoutSessions: state.planner.workoutSessions,
    plannerEntries: getWorkoutSectionTiles(state, userProfile),
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IDispatchProps => {
  const { userProfile } = ownProps.user;
  return {
    plannerFocused: () => dispatch(plannerOpened(ownProps.user.userProfile)),
    plannerDayPressed: (day: moment.Moment) => dispatch(plannerDayPressedAction(userProfile, day)),
    plannerEntryPressed: (booking: UserWorkoutSession) => dispatch(plannerEntryPressedAction(userProfile, booking)),
  };
};

export const Planner = translate(['dashboard'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(PlannerComponent),
);
